import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useState, memo } from "react";
// Sub-components imports...
import { IAutoPartsFreeShipping } from "api/interfaces/static-pages/auto-parts-free-shipping-canada.type";
import CmsContent from "component/CmsContent";
import PAButton, { BUTTON_SIZE, BUTTON_TYPE } from "component/design-system/buttons/PAButton";
import PAImage from "component/design-system/images/PAImage";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import FaqAccordion from "container/FaqAccordion";
import LandingPageBanner from "../../component/landing-page/LandingPageBanner";
import PageContainer from "../../modules/components/PageContainer";
import { getRelativeImagePath } from "../../util/helper/getRelativeImagePath";
import LinkCard from "./link-card.component";
import ShippingPerks from "./shipping-perks.component";
import styles from "./shipping_information.module.scss";

const termsConditionsContentIds = [
  "auto-parts-free-shipping-canada-terms-conditions",
  "fr-auto-parts-free-shipping-canada-terms-conditions",
];

const DeliveryStat = ({ percentage, description }) => (
  <div className="d-flex flex-column col-sm-4 p-lr-0-xs col-xs-12 align-items-center">
    <div className="d-flex align-items-center p-t-20">
      <PAImage
        src={getRelativeImagePath("/cms/pubicImages/images/lightening.png")}
        width={60}
        height={60}
      />
      <PAText className={`text-center m-0 ${styles.large_text}`}>{percentage}</PAText>
    </div>
    <PAText fontSize={FONT_SIZE[4]} className={`text-center p-t-20 ${styles.w_50_large}`}>{description}</PAText>
  </div>
);

const DeliveryPartners = () => {
  const { t } = useTranslation("static-pages");

  return (
    <div
      className={`m-t-20 p-t-20 d-flex flex-wrap flex-column align-items-center ${styles.each_section}`}
      style={{ backgroundColor: "#ffffff" }}
    >
      <PAText component="h2" color="black" bold fontSize={FONT_SIZE[5]} className="text-center p-t-30 p-b-30">{t("Our delivery Partners")}</PAText>
      <div className={`d-flex justify-content-between flex-wrap ${styles.delivery_image_container}`}>
        {[
          { name: "canadaPost", image: "pa-canadaPost.webp" },
          { name: "purolator", image: "pa-purolator.webp" },
          { name: "loomisExpress", image: "pa-loomisExpress.webp" },
          { name: "canpar", image: "pa-canpar.webp" },
        ].map((partner) => (
          <PAImage
            key={partner.name}
            src={getRelativeImagePath(`/cms/pubicImages/images/${partner.image}`)}
            alt={partner.name}
            title={partner.name}
            height={150}
            className={styles.img_shippingProvider}
          />
        ))}
      </div>
    </div>
  );
};

function ShippingInformation(props: IAutoPartsFreeShipping) {
  const response = props.actualResponse.landingPage;
  const { responsiveBannerMedia, headers } = response;
  const { contentIds } = response.links;
  const [isMounted, setIsMounted] = useState(false);
  const { t } = useTranslation("static-pages");
  const router = useRouter();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  function shopNow(e: any) {
    e?.preventDefault();
    router.push("/replacement-parts");
  }

  const shippingPerks = [
    {
      title: "shipping_perks_title_one",
      subTitle: "shipping_perks_sub_title_one",
      src: "/cms/pubicImages/images/SaveMoneyCanadian.webp",
    },
    {
      title: "shipping_perks_title_two",
      subTitle: "shipping_perks_sub_title_two",
      src: "/cms/pubicImages/images/SaveTimeCanadian.webp",
    },
    {
      title: "shipping_perks_title_three",
      subTitle: "shipping_perks_sub_title_three",
      src: "/cms/pubicImages/images/PeaceofMindCanadian.webp",
    },
    {
      title: "shipping_perks_title_four",
      subTitle: "shipping_perks_sub_title_four",
      src: "/cms/pubicImages/images/LargestSelectionCanadian.webp",
    },
  ];

  return (
    <PageContainer>
      <LandingPageBanner
        className="m-t-10"
        style={{ height: "auto" }}
        bannerImages={{
          mobileImage: getRelativeImagePath(`/cms/${responsiveBannerMedia.squareImage.url}`),
          desktopImage: getRelativeImagePath(`/cms/${responsiveBannerMedia.desktop.url}`),
        }}
        {...{
          width: 1340, height: 410, applyStyle: false,
        }}
        alt="shipping information"
      />
      <div className="d-flex justify-content-between flex-wrap m-t-20 p-t-20">
        <ShippingPerks data={shippingPerks} />
      </div>
      <div className="p-t-20 p-b-10 d-flex justify-content-center">
        <PAButton style={{ width: 250 }} buttonType={BUTTON_TYPE.OUTLINE} size={BUTTON_SIZE.LARGE} onClick={shopNow}>SHOP NOW</PAButton>
      </div>

      <div className={`m-t-20 p-t-20 ${styles.each_section}`}>
        <PAText component="h1" color="black" bold fontSize={FONT_SIZE[5]} className="text-center p-t-30">{headers.h1}</PAText>
        <PAText className="text-center p-t-10 d-flex w-100 justify-content-center" color="grey" fontSize={FONT_SIZE[2]}>{t("shipping_sub_title")}</PAText>

        <div className="d-flex justify-content-between flex-wrap m-t-20 m-b-20">
          <DeliveryStat percentage="91%" description={t("Orders ship within 24 hours")} />
          <DeliveryStat percentage="76%" description={t("Orders delivered within 3 days")} />
          <DeliveryStat percentage="95%" description={t("Orders delivered within 5 days")} />
        </div>

        <div className="d-flex justify-content-center  p-t-30">
          <PAText className="text-center p-t-10 d-flex w-100 justify-content-center" color="grey" fontSize={FONT_SIZE[2]}>
            {t("shipping_info_estimate_delivery_stat")}
          </PAText>
        </div>
      </div>

      <DeliveryPartners />

      <div className="d-flex justify-content-center flex-wrap m-t-20 p-t-20 align-items-center">
        <PAText component="h2" color="black" bold fontSize={FONT_SIZE[5]} className="text-center">{t("Frequently Asked Questions")}</PAText>
        {
          contentIds.map((eachContent) => {
            const { content, uiType } = eachContent?.uiDetails || {};
            if (uiType === "FAQ" && isMounted) {
              return (
                <FaqAccordion faqs={content.faqs} faqColCount={2} />
              );
            }
            return null;
          })
        }
      </div>

      <div className="d-flex flex-column flex-wrap m-t-20 p-t-20">
        <PAText component="h2" color="black" bold fontSize={FONT_SIZE[5]} className="text-center p-b-20">{t("Need parts sooner? We have faster options (for select areas)")}</PAText>
        <div className="d-flex justify-content-between flex-wrap">
          <div className="col-sm-6 d-flex m-b-5 p-lr-0-xs col-xs-12"><LinkCard title={t("In-store pick-up")} imageUrl="/cms/pubicImages/images/pa-instore-pickup.webp" subheading={t("instorePickUpMessage")} link="/in-store-pickup" /></div>
          <div className="col-sm-6 d-flex m-b-5 p-lr-0-xs col-xs-12"><LinkCard title={t("Rapid delivery")} imageUrl="/cms/pubicImages/images/pa-rapid-delivery.webp" subheading={t("rapidDeliveryMessage")} link="/rapid-delivery"> </LinkCard></div>
        </div>
      </div>

      <div className="d-flex justify-content-between flex-wrap m-t-20 p-t-20">
        <div className="col-sm-12  d-flex m-b-5  p-lr-0-xs  col-xs-12">
          <div className={`d-flex flex-column  ${styles.grey_background}`}>
            <PAText component="h2" color="black" bold fontSize={FONT_SIZE[5]} className="text-center p-b-20">{t("Terms & Conditions")}</PAText>
            {
              contentIds.map((eachContent) => {
                const { content, uiType } = eachContent?.uiDetails || {};
                const contentId = eachContent.url;
                if (termsConditionsContentIds && !(termsConditionsContentIds.includes(contentId))) {
                  return null;
                }
                if (uiType === "STATIC_PAGE" && isMounted) {
                  return (
                    <CmsContent key={contentId} className={styles.static_page} content={content?.article} />
                  );
                }
              })
            }
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

export default memo(ShippingInformation);
